'use client'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import styles from './faq-page.module.scss'
import RichText from '../components/RichText'
import ScaledImage from '../components/ScaledImage'
import TextButton from '../components/TextButton'
import DropdownIcon from '../assets/dropdown.svg'
import Collapsible from '../components/Account/Collapsible'
import richTextRenderer from '../utils/richTextRenderer'
import extractQueryParamFromPageLocation from '@src/quince-site-common/src/utils/extractQueryParamFromPageLocation'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'

const QUINCE_FAQ_PAGE_SLUG = '/faq'

const ImageWrapper = ({ children }) => <div className={styles.imageWrapper}>{children}</div>

const MobileImageWrapper = ({ children }) => <div className={styles.mobileImageWrapper}>{children}</div>

const TitleContainer = ({ children }) => <div className={styles.titleContainer}>{children}</div>

const TitleText = ({ children }) => <span className={styles.titleText}>{children}</span>

const Content = ({ children }) => <div className={styles.content}>{children}</div>

const TabsWrapper = ({ children }) => <div className={styles.tabsWrapper}>{children}</div>

const MobileSelectWrapper = ({ children }) => (
  <div className={styles.mobileSelectWrapper}>
    {children}
    <DropdownIcon className={styles.dropdown} />
  </div>
)

const MobileSelect = props => <select className={styles.mobileSelect} {...props} />

const ContactDetails = ({ children }) => <div className={styles.contactDetails}>{children}</div>

const RichTextContainer = ({ children }) => <div className={styles.richTextContainer}>{children}</div>

const Dropdown = ({ children }) => <div className={styles.dropdown}>{children}</div>

// const RichTextWrapper = ({ children, ...props }) => (
//   <div className={styles.richTextWrapper} {...props}>
//     {children}
//   </div>
// )

const StyledTabs = ({ children }) => <div className={styles.styledTabs}>{children}</div>

const NavList = ({ children }) => <ul className={styles.navList}>{children}</ul>

const NavItem = ({ children }) => <li className={styles.navItem}>{children}</li>

const StyledTextButton = ({ children, ...props }) => (
  <TextButton className={`${styles.styledTextButton} ${props.active ? styles.activeBtn : ''}`} {...props}>
    {children}
  </TextButton>
)

const StyledRichText = ({ json }) => <RichText className={styles.styledRichText} json={json} />

const StyledCollapsible = ({ children, ...props }) => (
  <div className={styles.styledCollapsible} {...props}>
    {children}
  </div>
)

const NavBar = ({ children }) => <div className={styles.navBar}>{children}</div>

const showContact = slug => {
  return !['shipping-returns', 'faq'].some(noContactSlug => noContactSlug === slug)
}

const getContent = contentNodes => {
  let qIndex = 0

  let qna = { question: null, answers: [] }
  const faqs = []
  while (qIndex < contentNodes.length) {
    if (contentNodes[qIndex].type === 'h2') {
      if (qIndex === 0) {
        qna.question = contentNodes[qIndex]
      } else {
        // Hit next question
        faqs.push({ ...qna }) // Push qna set
        qna = { question: contentNodes[qIndex], answers: [] } // and start new set
      }
    } else {
      qna.answers.push(contentNodes[qIndex])
    }

    if (qIndex + 1 === contentNodes.length) {
      // Push last qna set
      faqs.push({ ...qna })
    }

    qIndex += 1
  }

  return faqs
}

const getFaqContent = (id, text) => {
  const section = {
    id,
    content: getContent(richTextRenderer(text.json)),
  }

  return (
    <StyledCollapsible className={styles.styledCollapsible} id={section.id}>
      {section.content.map(qnaItem => (
        <Collapsible key={qnaItem.question} title={qnaItem.question} startCollapsed>
          {qnaItem.answers}
        </Collapsible>
      ))}
    </StyledCollapsible>
  )
}

const FaqPage = ({
  data,
  // location
}) => {
  const page = data
  const { accordion } = page
  const activeSubSectionFromLink = isBrowser() ? extractQueryParamFromPageLocation('section') : ''
  const [active, setActive] = useState(0)
  const ref = useRef([])

  const scrollTo = useCallback((ref, i) => {
    const vwToPx = value => {
      const viewWidth =
        window.innerWidth ||
        document.documentElement.clientWidth ||
        document.getElementsByTagName('body')[0].clientWidth
      const result = (viewWidth * value) / 100
      return result
    }

    const menuHeight = vwToPx(10)
    const bodyRect = document.body.getBoundingClientRect()
    const elRect = ref.current[i].getBoundingClientRect()
    const offset = elRect.top - bodyRect.top - menuHeight

    window.scrollTo({
      top: offset,
      // @i18n-ignore
      behavior: 'smooth',
    })
  }, [])

  useEffect(() => {
    // TODO-SSR: Figure out the use case of location and how to provide the state in location using nextJs
    // if (location?.state?.activeAccordion) {
    //   scrollTo(ref, location?.state?.activeAccordion);
    //   setActive(location?.state?.activeAccordion);
    // }
  }, [])

  useEffect(() => {
    const indexToScrollTo = accordion.findIndex(item => item.uniqueKey === activeSubSectionFromLink)
    if (indexToScrollTo !== -1) {
      scrollTo(ref, indexToScrollTo)
      setActive(indexToScrollTo)
    }
  }, [accordion, activeSubSectionFromLink, scrollTo])

  return (
    <>
      <ImageWrapper>
        <ScaledImage fluid={page.image} />
      </ImageWrapper>

      <MobileImageWrapper>
        <ScaledImage fluid={page.mobileImage} useNative />
      </MobileImageWrapper>

      <TitleContainer>
        <TitleText> {page.title} </TitleText>
      </TitleContainer>

      {accordion && (
        <MobileSelectWrapper>
          <MobileSelect
            value={accordion[0].id}
            onChange={e => {
              const selectedAccordionIndex = accordion.findIndex(items => items.id === e.target.value)

              const accordionItem = accordion[selectedAccordionIndex]
              const currentUrl = new URL(window.location)
              currentUrl.searchParams.set('section', accordionItem?.uniqueKey)
              window.history.pushState({}, '', currentUrl.toString())
              scrollTo(ref, selectedAccordionIndex)
            }}
          >
            {accordion.map(({ id, title }) => (
              <option value={id} key={id}>
                {title}
              </option>
            ))}
          </MobileSelect>
          <Dropdown />
        </MobileSelectWrapper>
      )}

      <Content>
        {accordion && (
          <TabsWrapper>
            <StyledTabs>
              <NavBar>
                <NavList>
                  {accordion.map(({ id, title, uniqueKey }, i) => (
                    <NavItem key={id}>
                      <StyledTextButton
                        onClick={() => {
                          const currentUrl = new URL(window.location)
                          currentUrl.searchParams.set('section', uniqueKey)
                          window.history.pushState({}, '', currentUrl.toString())
                          setActive(i)
                          scrollTo(ref, i)
                        }}
                        asAnchor
                        active={active === i}
                      >
                        {title}
                      </StyledTextButton>
                    </NavItem>
                  ))}
                </NavList>
                {showContact(page.slug) && <ContactDetails>{richTextRenderer(page.contact.json)}</ContactDetails>}
              </NavBar>

              {/* To show the content */}
              <RichTextContainer>
                {accordion.map(({ id, text }, index) => (
                  <div className={styles.richTextWrapper} key={id} ref={el => (ref.current[index] = el)}>
                    {page.slug === QUINCE_FAQ_PAGE_SLUG ? (
                      getFaqContent(id, text)
                    ) : (
                      <StyledRichText key={id} json={text.json} />
                    )}
                  </div>
                ))}
              </RichTextContainer>
            </StyledTabs>
          </TabsWrapper>
        )}
      </Content>
    </>
  )
}

export default FaqPage
